import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Member } from '@/models/Member';
import { useToastMessages } from '@/hooks/useToastMessages';
import { MembersService } from '@/services/domain/MembersService';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Button, ButtonText } from '@/components/gluestack/button';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from '@/components/gluestack/alert-dialog';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Cancel01Icon } from '@hugeicons-pro/core-solid-rounded';
import { CheckmarkSquare02Icon } from '@hugeicons-pro/core-stroke-rounded';

interface UnblockButtonProps {
  member: Member;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  hideIcon?: boolean;
  /**
   * When provided, the button will also remove the member from the specified
   * query lists after a successful unblock. Use a common prefix like 'members'.
   */
  cacheQueryKeyPrefix?: string;
}

export function UnblockButton({
  member,
  className,
  size = 'sm',
  hideIcon = false,
  cacheQueryKeyPrefix,
}: UnblockButtonProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { removeCacheItem } = useQueryCacheUtils();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleUnblock = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MembersService.unblockMember(member.Id);

      // Optionally remove the unblocked member from cached lists
      if (cacheQueryKeyPrefix) {
        removeCacheItem(cacheQueryKeyPrefix, member.Id, 'MembersBasic');
      }

      showSuccessToast(t('member.unblock.success'));
    } catch (error) {
      showErrorToast(t('member.unblock.error'), error as any);
    } finally {
      setIsLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <ButtonWrapper
        icon={hideIcon ? undefined : CheckmarkSquare02Icon}
        text={t('common.unblock')}
        size={size}
        action="primary"
        variant="solid"
        className={className}
        onPress={() => setShowConfirm(true)}
        isDisabled={isLoading}
      />

      <AlertDialog isOpen={showConfirm} onClose={() => setShowConfirm(false)} size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('member.menu.unblock')}</Heading>
            <AlertDialogCloseButton>
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('member.unblock.confirmMessage', { userName: member.UserName })}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button variant="outline" action="neutral" onPress={() => setShowConfirm(false)} disabled={isLoading}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="primary" onPress={handleUnblock} disabled={isLoading}>
              <ButtonText>{t('common.continue')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


